import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { ShieldCheck, ArrowsClockwise, CopySimple, Warning } from "@phosphor-icons/react";

export function BackupCodes() {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [codes, setCodes] = useState(null);
  const { data } = useQuery({ queryKey: ["backup-codes-status"], queryFn: () => api.get("/auth/mfa/backup-codes").then((r) => r.data) });

  const regen = useMutation({
    mutationFn: () => api.post("/auth/mfa/backup-codes/regenerate"),
    onSuccess: ({ data }) => {
      setCodes(data.backup_codes);
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ["backup-codes-status"] });
      toast.success("New backup codes generated — your old codes no longer work");
    },
    onError: (e) => { setConfirming(false); toast.error(errText(e)); },
  });

  const copyAll = async () => {
    const text = codes.join("\n");
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    toast.success("All 8 codes copied — store them somewhere safe");
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="backup-codes-card">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><ShieldCheck size={15} /> MFA backup codes</h3>
      <p className="text-[11px] text-zinc-500 mb-3">
        One-time codes that work in place of your authenticator if you lose your phone.
        {" "}<span data-testid="backup-codes-remaining" className="font-medium text-zinc-700">{data?.remaining ?? "—"} of 8 remaining.</span>
      </p>

      {codes ? (
        <div data-testid="backup-codes-list" className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 mb-3">
          <p className="text-[10px] text-emerald-800 mb-2 flex items-center gap-1"><Warning size={11} /> Shown once — copy these now. Each code works one time.</p>
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {codes.map((c) => <span key={c} className="font-mono text-[11px] text-zinc-800 bg-white border border-zinc-200 rounded-sm px-1.5 py-1 text-center">{c}</span>)}
          </div>
          <button data-testid="backup-codes-copy-btn" onClick={copyAll} className="flex items-center gap-1 text-[11px] text-emerald-900 underline hover:no-underline"><CopySimple size={12} /> Copy all 8 codes</button>
        </div>
      ) : null}

      {confirming ? (
        <div className="flex items-center gap-2" data-testid="backup-codes-confirm">
          <p className="text-[11px] text-rose-700 flex-1">This invalidates all existing backup codes. Continue?</p>
          <Button data-testid="backup-codes-confirm-btn" size="sm" variant="destructive" className="h-7 text-[11px]" disabled={regen.isPending} onClick={() => regen.mutate()}>Yes, regenerate</Button>
          <Button data-testid="backup-codes-cancel-btn" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setConfirming(false)}>Cancel</Button>
        </div>
      ) : (
        <Button data-testid="backup-codes-regen-btn" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setConfirming(true)}>
          <ArrowsClockwise size={12} className="mr-1" /> {codes ? "Regenerate again" : "Generate new backup codes"}
        </Button>
      )}
    </div>
  );
}
